import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { useToastService } from '../../services/toast-service.tsx'
import { usePutProject } from '../../utils/api-client/api-hooks.ts'
import type { Project } from '../../utils/api-client/api-schemas.ts'
import type { AvailableTemplate } from '../../utils/api-client/templates.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { useShowProjectMutationError } from '../projects/admin-required-override.ts'
import { isDuplicateProjectNameError } from '../projects/project-constants.ts'
import { ProjectDetailsFields } from '../projects/project-details-fields.tsx'
import * as styles from '../projects/project-dialog.css.ts'

export type EditProjectModalProps = {
  project: Pick<Project, 'id' | 'name' | 'description'>
  template: AvailableTemplate
  onClose: () => unknown
  onSaved: (project: Project) => unknown
  /**
   * Returns focus to whatever opened this dialog. Radix restores to a `Dialog.Trigger`, and this
   * dialog has none — it mounts already open. @see useReturnFocus
   */
  onCloseAutoFocus?: (event: Event) => void
}

/**
 * The only way to change a project's name or description. Both fields together, prefilled, in the same
 * shape the create dialog uses — there is no inline rename on the details page, so this is one path and
 * one set of states rather than two.
 */
export const EditProjectModal: React.FC<EditProjectModalProps> = ({
  project,
  template,
  onClose,
  onSaved,
  onCloseAutoFocus,
}) => {
  const { addToast } = useToastService()
  const [name, setName] = useState(project.name)
  const [description, setDescription] = useState(project.description ?? '')
  const [nameError, setNameError] = useState<string>()

  const showError = useShowProjectMutationError('Failed to save project')
  const saveMutation = usePutProject({
    onSuccess: saved => {
      addToast({ message: <>Project &ldquo;{saved.name}&rdquo; saved</>, variant: 'success' })
      onSaved(saved)
    },
    onError: (error, variables, ...rest) => {
      // A taken name is the one failure the typist can fix, so the dialog stays open with the reason.
      // Named from what was submitted rather than from the box, which they may have kept typing into.
      if (isDuplicateProjectNameError(error)) {
        setNameError(`A project named ${variables.body.name} already exists.`)
        return
      }
      showError(error, variables, ...rest)
    },
  })

  const isPending = saveMutation.isPending
  const trimmedName = name.trim()
  const trimmedDescription = description.trim()

  const handleSave = () => {
    if (trimmedName === '') {
      return
    }
    setNameError(undefined)
    saveMutation.mutate({
      projectId: project.id,
      template,
      // An emptied field is a removal, not an omission, so it goes as null rather than being left out.
      body: { name: trimmedName, description: trimmedDescription === '' ? null : trimmedDescription },
    })
  }

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onPointerDownOutside={event => event.preventDefault()}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Dialog.Title className={styles.title}>Edit project</Dialog.Title>
          <Dialog.Description hidden>Change the project&apos;s name and description.</Dialog.Description>

          <ProjectDetailsFields
            name={name}
            onNameChange={value => {
              setName(value)
              setNameError(undefined)
            }}
            nameError={nameError}
            description={description}
            onDescriptionChange={setDescription}
          />

          <div className={styles.barEnd}>
            <GhostButton
              type="secondary"
              className={styles.outlinedGhost}
              onClick={onClose}
              state={isPending ? 'disabled' : 'default'}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              state={isPending ? 'loading' : trimmedName === '' ? 'disabled' : 'default'}
              onClick={handleSave}
              loadingLabel="Saving project"
            >
              Save
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
